import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { AnalyticsSummary } from '../../../shared/api/analytics.api'
import { fmtInt } from './summaryFormat'

const OUTCOME_DEFS: { key: keyof AnalyticsSummary['outcomes']; label: string; color: string }[] = [
  { key: 'won', label: 'В заказ', color: '#10b981' },
  { key: 'kp', label: 'КП выставлено', color: '#3b82f6' },
  { key: 'inWork', label: 'В работе', color: '#f59e0b' },
  { key: 'lost', label: 'Отказ', color: '#ef4444' },
  { key: 'archived', label: 'В архиве', color: '#9ca3af' },
]

export function SummaryFunnel({ data }: { data: AnalyticsSummary }) {
  const maxFunnel = Math.max(1, ...data.funnel.map((s) => s.count))
  const o = data.outcomes
  const pieData = OUTCOME_DEFS.map((d) => ({
    name: d.label,
    value: o[d.key],
    color: d.color,
  })).filter((d) => d.value > 0)

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* Сквозная воронка */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-900">Воронка заявок за период</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Путь заявок, созданных в периоде: создано → в заказ. % — от числа созданных.
        </p>
        <div className="mt-3 space-y-1.5">
          {data.funnel.map((s) => (
            <div key={s.key} className="flex items-center gap-3">
              <div className="w-32 text-sm text-gray-700 flex-shrink-0 truncate" title={s.label}>
                {s.label}
              </div>
              <div className="flex-1 bg-gray-100 rounded-md h-6 overflow-hidden">
                <div
                  className="h-full rounded-md bg-brand-500 transition-all"
                  style={{ width: `${(s.count / maxFunnel) * 100}%` }}
                />
              </div>
              <div className="w-24 text-right text-xs text-gray-500 flex-shrink-0 tabular-nums">
                {fmtInt(s.count)} ·{' '}
                <span className="font-medium text-gray-700">{s.conversion}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Исходы заявок периода */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-900">Исходы заявок периода</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Текущее состояние заявок, созданных в периоде ({fmtInt(o.total)} шт).
        </p>
        <div className="mt-2 flex items-center gap-4">
          <div className="w-40 h-40 flex-shrink-0">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={40}
                    outerRadius={72}
                    paddingAngle={2}
                  >
                    {pieData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [fmtInt(Number(v)), String(n)]}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-400">
                Нет данных
              </div>
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            {OUTCOME_DEFS.map((d) => {
              const val = o[d.key]
              const share = o.total > 0 ? Math.round((val / o.total) * 100) : 0
              return (
                <div key={d.key} className="flex items-center gap-2 text-sm">
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: d.color }}
                  />
                  <span className="text-gray-600 flex-1">{d.label}</span>
                  <span className="tabular-nums text-gray-900 font-medium">{fmtInt(val)}</span>
                  <span className="tabular-nums text-gray-400 w-10 text-right">{share}%</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
